import { useState, type CSSProperties, type ReactNode } from "react";
import { Avatar, Button, Select, TextField, vars } from "@pack-it-up/ui-web";
import type { ParticipantId } from "@pack-it-up/shared";
import type { TemplateParticipants, UserProfile } from "@pack-it-up/core";

// Issue #185 — a template's default travelers. Adding one here is what makes the
// template add that person to a trip when it is imported, and what its rows can be
// assigned to.
//
// Issue #188 put it inside the Share dialog, below the people the template is shared
// with: both answer "who is involved", and the dialog is where the distinction has to
// be made — sharing a template decides who may *edit* it, this decides who travels.
// It therefore renders as a dialog section (no card of its own).
//
// A traveler is either a name with no login (a child) or one of your friends, whom the
// import adds to the trip as an editor — so the picker says so.

// Matches the share dialog's own section headings.
const labelStyle: CSSProperties = {
  fontSize: vars.font.sizeXs,
  fontWeight: vars.font.weightSemibold,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  color: vars.color.textMuted,
  marginBottom: vars.space.xs,
};

const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: vars.space.sm,
  padding: `${vars.space.xs} 0`,
};

export const TemplateTravelers = ({
  participants,
  friends,
  canEdit,
  onAddManaged,
  onAddUser,
  onRemove,
}: {
  participants: TemplateParticipants;
  friends: UserProfile[];
  canEdit: boolean;
  onAddManaged: (displayName: string) => void;
  onAddUser: (friend: UserProfile) => void;
  onRemove: (participantId: ParticipantId) => void;
}): ReactNode => {
  const [name, setName] = useState("");
  const entries = Object.entries(participants) as Array<[ParticipantId, TemplateParticipants[ParticipantId]]>;
  const alreadyAdded = new Set(entries.map(([id]) => String(id)));
  const addableFriends = friends.filter((f) => !alreadyAdded.has(String(f.userId)));

  const addManaged = (): void => {
    const trimmed = name.trim();
    if (trimmed === "") return;
    onAddManaged(trimmed);
    setName("");
  };

  return (
    <section aria-label="Travelers">
      <h2 style={{ ...labelStyle, margin: 0, marginBottom: vars.space.xs }}>Going on the trip</h2>
      <p style={{ fontSize: vars.font.sizeXs, color: vars.color.textMuted, margin: 0, marginBottom: vars.space.sm }}>
        Added to the trip when you import this template. Rows can be assigned to them —
        they are not given access to the template itself.
      </p>

      {entries.length === 0 && (
        <p style={{ fontSize: vars.font.sizeSm, color: vars.color.textMuted, margin: 0 }}>No travelers yet.</p>
      )}

      {entries.map(([id, p]) => (
        <div key={id} style={rowStyle}>
          <Avatar name={p.displayName} size="sm" />
          <span style={{ flex: 1, minWidth: 0, fontSize: vars.font.sizeSm, color: vars.color.text }}>
            {p.displayName}
            {p.kind === "user" && (
              <span style={{ marginLeft: vars.space.xs, color: vars.color.textMuted, fontSize: vars.font.sizeXs }}>
                {p.email}
              </span>
            )}
          </span>
          {canEdit && (
            <Button variant="ghost" size="sm" aria-label={`Remove ${p.displayName}`} onClick={() => onRemove(id)}>
              Remove
            </Button>
          )}
        </div>
      ))}

      {canEdit && (
        <div style={{ display: "flex", alignItems: "flex-end", gap: vars.space.sm, marginTop: vars.space.sm }}>
          <TextField
            label="Add a traveler"
            placeholder="Name (e.g. a child)"
            value={name}
            onChange={setName}
            onKeyDown={(e) => {
              if (e.key === "Enter") addManaged();
            }}
          />
          <Button variant="secondary" size="sm" onClick={addManaged}>
            Add
          </Button>
          {addableFriends.length > 0 && (
            <Select
              label="Or add a friend"
              value=""
              onChange={(userId) => {
                const friend = addableFriends.find((f) => String(f.userId) === userId);
                if (friend !== undefined) onAddUser(friend);
              }}
              options={[
                { value: "", label: "Choose a friend…" },
                ...addableFriends.map((f) => ({ value: String(f.userId), label: f.displayName })),
              ]}
            />
          )}
        </div>
      )}
    </section>
  );
};
